import { DropdownActionItem, DropdownActions, DropdownItemIcon } from '../../../../../../widgets/dropdown';
import { ConfirmDropdownItem } from '../../../configurator-components/confirmTwice';
import { ComposerMenuHint } from './composerMenuHint';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC } from 'react';
import { T } from 'src/shared/helpers';
import RemoveIcon from 'src/shared/material_icons/action/delete.svg';
import VisibilityIcon from 'src/shared/material_icons/action/visibility.svg';
import VisibilityOffIcon from 'src/shared/material_icons/action/visibility_off.svg';
import LinkIcon from 'src/shared/material_icons/content/link.svg';
import DuplicateIcon from 'src/shared/material_icons/content/content_copy.svg';
import ImageIcon from 'src/shared/material_icons/image/image.svg';
import MoreIcon from 'src/shared/material_icons/navigation/more_vert.svg';
import PlayIcon from 'src/shared/material_icons/av/play_arrow.svg';
import RetargetIcon from 'src/shared/material_icons/maps/my_location.svg';
import CopyToIcon from 'src/shared/material_icons/content/copy_all.svg';
import MoveToIcon from 'src/shared/material_icons/file/drive_file_move.svg';
import ReplaceInIcon from 'src/shared/material_icons/action/find_replace.svg';
import PreviewImageIcon from 'src/shared/material_icons/image/add_photo_alternate.svg';
import styled, { css } from 'styled-components';
import { markMatches } from '../../../configurator-components/TreeComponent';
import { ComposerRowActionButton as ActionButton } from '../../../configurator-components/composer-editors/composerWorkspace';
import { ComposerAttributeOptionNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { getComposerOptionValidationMessages } from '../helpers/composerAttributeValidation';
import ComposerValidationTooltip from './composerValidationTooltip';
import { playComposerOption } from '../helpers/composerPlay';
import usePreview3DStore from 'src/components/widgets/preview3D/store';

export type ComposerOptionRowAction = 'links' | 'duplicate' | 'move' | 'copy' | 'replace' | 'image' | 'retarget' | 'remove';

interface ComposerOptionTreeElementProps {
    option: ComposerAttributeOptionNode;
    onSelect: (event: React.MouseEvent<HTMLDivElement>, option: ComposerAttributeOptionNode) => void;
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>, option: ComposerAttributeOptionNode) => void;
    onMoveUp?: (option: ComposerAttributeOptionNode) => void;
    onMoveDown?: (option: ComposerAttributeOptionNode) => void;
    /** La riga intera si trascina (utente, 2026-10-15): il gesto lo conduce chi ospita le righe (`startPointerReorder`). */
    onRowPointerDown?: (event: React.PointerEvent<HTMLElement>, option: ComposerAttributeOptionNode) => void;
    /** La profondità della riga nell'albero: dice al trascinamento di chi è figlia. */
    level?: number;
    onSetDefault?: (option: ComposerAttributeOptionNode) => void;
    onAction?: (action: ComposerOptionRowAction, option: ComposerAttributeOptionNode) => void;
    onVisibilityClick?: (option: ComposerAttributeOptionNode) => void;
    selected: boolean;
    /** La riga è scelta insieme ad altre: il «…» parla della selezione, e offre anche l'immagine di anteprima. */
    inSelection?: boolean;
    hasLink?: boolean;
    indent?: number;
    highlight?: string;
}

const Row = styled.div<{ $selected: boolean; $indent: number }>`
    position: relative;
    display: flex;
    min-width: 0;
    height: 34px;
    align-items: center;
    gap: 6px;
    /* IL RIENTRO STA DENTRO LA RIGA (prototipo, .tree-block .row; utente 2026-10-21): fondo e filo della riga scelta
       vanno da un bordo all'altro della card, il nome si sposta col livello. */
    padding: 0 6px 0 ${(props) => props.$indent + 6}px;
    color: var(--editor-fg, #262a30);
    background: ${(props) => (props.$selected ? 'var(--editor-accent-wash, #fdf0e6)' : 'transparent')};
    /* La riga scelta: lavato e filo arancio, come in ogni albero dei due editor (treeRowLook). */
    box-shadow: ${(props) => (props.$selected ? 'inset 0 0 0 1px var(--editor-accent, #a94700)' : 'none')};
    border: 0;
    border-radius: 8px;
    cursor: pointer;
    outline: none;
    user-select: none;

    &:hover,
    &:focus-visible {
        background: ${(props) => (props.$selected ? 'var(--editor-accent-wash, #fdf0e6)' : 'var(--editor-field, #f1f2f4)')};
    }

    /* Row commands appear on hover, except the ones marked persistent: default, constraints and a disabled option must read at a glance. */
    > div:last-child > *:not([data-persistent]) {
        opacity: 0;
        transition: opacity 110ms ease;
    }

    /* :focus-within alone would keep them lit after a plain click, since the row itself takes focus. */
    &:hover > div:last-child > *,
    &:focus-within:not(:focus) > div:last-child > * {
        opacity: 1;
    }
`;

// SPENTA SI SBIADISCONO LA MINIATURA E IL NOME (prototipo): il pallino del default resta leggibile.
const Main = styled.div<{ $muted: boolean }>`
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    gap: 8px;

    ${(props) =>
        props.$muted &&
        css`
            > [data-row-glyph],
            > [data-row-name] {
                opacity: 0.45;
            }
        `}
`;

// IL DEFAULT È UN PALLINO DAVANTI ALLA MINIATURA (prototipo, decisione 19/10): uno per attributo, come un radio.
// Si vede sempre su quella di default, sulle altre al passaggio.
const DefaultRadio = styled.button`
    /* IL PALLINO È DI 16, IL BERSAGLIO DI 24 (utente, 2026-10-22, WCAG 2.5.8): il bottone è grande 24 e i margini
       negativi lo tengono nel posto di prima; il cerchio è disegnato dentro, a 16. */
    --ring: var(--editor-faint, #676a6e);
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin: -4px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: radial-gradient(circle closest-side, transparent 6.5px, var(--ring) 6.5px, var(--ring) 8px, transparent 8px);
    cursor: pointer;
    opacity: 0;
    transition: opacity 110ms ease;

    ${Row}:hover &,
    &:focus-visible,
    &[aria-checked='true'] {
        opacity: 1;
    }

    &:hover {
        --ring: var(--editor-muted, #55595f);
    }

    &[aria-checked='true'] {
        --ring: var(--editor-accent, #a94700);
    }

    > i {
        width: 8px;
        height: 8px;
        border-radius: 50%;
    }

    &[aria-checked='true'] > i {
        background: var(--editor-accent, #a94700);
    }
`;

// LA MINIATURA DA 26px, col Play sopra al passaggio (prototipo): l'opzione si prova sul modello da dove la si vede.
const Preview = styled.span`
    position: relative;
    display: inline-flex;
    width: 26px;
    height: 26px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    color: var(--composer-kind-option, #9a2368);

    > img,
    > svg {
        width: 100%;
        height: 100%;
        border-radius: 4px;
        object-fit: cover;
        fill: currentColor;
    }

    > svg {
        padding: 4px;
    }
`;

const ThumbPlay = styled.button`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: rgb(17 20 24 / 45%);
    color: #fff;
    cursor: pointer;
    opacity: 0;
    transition: opacity 110ms ease;

    ${Row}:hover &,
    &:focus-visible {
        opacity: 1;
    }

    &:hover {
        background: var(--editor-accent-wash, #f6efe9);
        color: var(--editor-accent, #a94700);
        box-shadow: inset 0 0 0 1px var(--editor-accent-line, #e2b597);
    }

    svg {
        width: 17px;
        height: 17px;
        fill: currentColor;
    }
`;

const Name = styled.span`
    overflow: hidden;
    font-size: 13px;
    /* Peso normale per ogni riga, come il prototipo (utente, 2026-10-15). */
    font-weight: 400;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Actions = styled.div`
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 1px;
`;

const Slot = styled.span`
    display: inline-flex;
    width: 24px;
    height: 24px;
`;

export const ComposerOptionTreeElement: FC<ComposerOptionTreeElementProps> = ({
    option,
    onSelect,
    onDoubleClick,
    selected,
    inSelection = false,
    hasLink,
    indent = 0,
    highlight,
    onMoveUp,
    onMoveDown,
    onRowPointerDown,
    level = 1,
    onSetDefault,
    onAction,
    onVisibilityClick
}) => {
    const validationMessages = getComposerOptionValidationMessages(option);
    const sceneEditor = usePreview3DStore((store) => store.sceneEditor);

    return (
        <Row
            $selected={selected}
            $indent={indent}
            data-composer-row={option.id}
            data-reorder-id={option.id}
            onPointerDown={onRowPointerDown ? (event) => onRowPointerDown(event, option) : undefined}
            data-reorder-level={level}
            aria-selected={selected}
            onClick={(event) => onSelect(event, option)}
            onDoubleClick={onDoubleClick ? (event) => onDoubleClick(event, option) : undefined}
            onKeyDown={(event) => {
                if (event.key === 'ArrowUp' && event.altKey) {
                    event.preventDefault();
                    onMoveUp?.(option);
                }
                if (event.key === 'ArrowDown' && event.altKey) {
                    event.preventDefault();
                    onMoveDown?.(option);
                }
            }}
            role='treeitem'
            tabIndex={0}
        >
            <Main $muted={option.enabled === false}>
                <ZTooltip title={option.isDefault ? T._('Default option', 'Admin') : T._('Set as default', 'Admin')}>
                    <DefaultRadio
                        type='button'
                        role='radio'
                        aria-checked={!!option.isDefault}
                        aria-label={option.isDefault ? T._('Default option', 'Admin') : T._('Set as default', 'Admin')}
                        onClick={(event) => {
                            event.stopPropagation();
                            onSetDefault?.(option);
                        }}
                    >
                        <i />
                    </DefaultRadio>
                </ZTooltip>
                <Preview data-reorder-anchor data-row-glyph>
                    {option.img ? <img src={option.img} alt='' /> : <ImageIcon />}
                    {option.actions.length > 0 && (
                        <ZTooltip title={T._('Play this option on the model', 'Admin')}>
                            <ThumbPlay
                                type='button'
                                aria-label={T._('Play this option on the model', 'Admin')}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    playComposerOption(sceneEditor, option);
                                }}
                            >
                                <PlayIcon />
                            </ThumbPlay>
                        </ZTooltip>
                    )}
                </Preview>
                <ZTooltip title={option.name} position='top' isOverflowEnabled>
                    <Name data-row-name>{markMatches(option.name, highlight)}</Name>
                </ZTooltip>
                {validationMessages.length > 0 && <ComposerValidationTooltip messages={validationMessages} />}
            </Main>
            <Actions>
                {hasLink ? (
                    <ZTooltip title={T._('This option has constraints', 'Admin')}>
                        <ActionButton
                            $active
                            aria-label={T._('Open constraints', 'Admin')}
                            data-persistent='true'
                            onClick={(event) => {
                                event.stopPropagation();
                                onAction?.('links', option);
                            }}
                        >
                            <LinkIcon />
                        </ActionButton>
                    </ZTooltip>
                ) : (
                    <Slot />
                )}
                <ZTooltip title={option.enabled !== false ? T._('Disable this option', 'Admin') : T._('Enable this option', 'Admin')}>
                    <ActionButton
                        $off={option.enabled === false}
                        aria-label={option.enabled !== false ? T._('Disable this option', 'Admin') : T._('Enable this option', 'Admin')}
                        aria-pressed={option.enabled === false}
                        data-persistent={option.enabled === false ? 'true' : undefined}
                        onClick={(event) => {
                            event.stopPropagation();
                            onVisibilityClick?.(option);
                        }}
                    >
                        {option.enabled !== false ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </ActionButton>
                </ZTooltip>
                <DropdownActions
                    header={
                        <ActionButton type='button' aria-label={T._('More commands', 'Admin')}>
                            <MoreIcon />
                        </ActionButton>
                    }
                    sections={[
                        [
                            <DropdownActionItem key='duplicate' icon={<DuplicateIcon />} onClick={() => onAction?.('duplicate', option)}>
                                {T._('Clone', 'Admin')}
                            </DropdownActionItem>,
                            // COPIARE IN UN ALTRO ATTRIBUTO DAL «…» (utente, 2026-10-15): l'elenco degli attributi si apre al
                            // posto del menu. L'immagine di anteprima solo per una selezione: quella di un'opzione sta nelle Settings.
                            <DropdownActionItem key='move' icon={<MoveToIcon />} onClick={() => onAction?.('move', option)}>
                                {T._('Move to…', 'Admin')}
                            </DropdownActionItem>,
                            <ComposerMenuHint key='copy' hint={T._('Add a copy of the options to another attribute', 'Admin')}>
                                <DropdownActionItem key='copy' icon={<CopyToIcon />} onClick={() => onAction?.('copy', option)}>
                                    {T._('Copy to…', 'Admin')}
                                </DropdownActionItem>
                            </ComposerMenuHint>,
                            <ComposerMenuHint key='replace' hint={T._('Replace the options of another attribute with these', 'Admin')}>
                                <DropdownActionItem key='replace' icon={<ReplaceInIcon />} onClick={() => onAction?.('replace', option)}>
                                    {T._('Replace in…', 'Admin')}
                                </DropdownActionItem>
                            </ComposerMenuHint>,
                            inSelection && (
                                <ComposerMenuHint key='image' hint={T._('Set the preview image of all the selected options', 'Admin')}>
                                    <DropdownActionItem key='image' icon={<PreviewImageIcon />} onClick={() => onAction?.('image', option)}>
                                        {T._('Preview image…', 'Admin')}
                                    </DropdownActionItem>
                                </ComposerMenuHint>
                            ),
                            <ComposerMenuHint key='retarget' hint={T._('Point the actions at other objects and materials', 'Admin')}>
                                <DropdownActionItem key='retarget' icon={<RetargetIcon />} onClick={() => onAction?.('retarget', option)}>
                                    {T._('Retarget', 'Admin')}
                                </DropdownActionItem>
                            </ComposerMenuHint>
                        ],
                        [
                            // Rimuovere chiede due volte, nella voce stessa (confirmTwice).
                            <ConfirmDropdownItem
                                key='remove'
                                icon={
                                    <DropdownItemIcon>
                                        <RemoveIcon />
                                    </DropdownItemIcon>
                                }
                                onConfirm={() => onAction?.('remove', option)}
                            >
                                {T._('Remove', 'Admin')}
                            </ConfirmDropdownItem>
                        ]
                    ]}
                    minWidth='236px'
                    orientation='RIGHT'
                />
            </Actions>
        </Row>
    );
};
